function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { ToastType } from 'common/components/toast/types';
import { addToast } from 'common/components/toast-area/toast-manager';
import { ModalType } from 'common/components/modal';
import { capitalize } from 'common/util';
import { Cohort } from 'common/query-builder/cohorts-query-builder/models/cohort';
import { FeatureGate, ProjectFeatureFlag, ReportName, RestrictEntitySaving } from 'common/report/constants';
import { getReportUrl } from 'common/report/util';
import { cohortsStore } from 'common/report/stores/cohorts';
import { reporter } from 'common/report/error-reporting';
import { buildCreateEntityModalProps, isSharingEnabledForProject, normalizeCohortEntity } from 'common/report/util/saved-entities';
import { cohortsClient } from 'common/report/queries/app-api/cohorts-client';
import { fetchPeopleCountByCohort } from 'common/report/queries/query-api/people-stats-client';
import { EntityType, SharableEntityType } from 'common/report/util/saved-entities/types';
import { SerializedCohort } from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import { CohortsQueryBuilderUpdate } from 'common/query-builder/cohorts-query-builder/types';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useTranslation } from 'common/i18n/hooks';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { InputReact } from 'common/components/input';
import { ModalReact } from 'common/components/modal';
import { SpinnerReact } from 'common/components/spinner';
import { TooltipReact } from 'common/components/tooltip';
import { Placement } from 'common/components/tooltip/types';
import { BannerReact } from 'common/components/banner';
import { BannerTheme, BannerType } from 'common/components/banner/types';
import { SkeletonListReact } from 'common/components/skeleton-list';
import { SvgIconReact } from 'common/components/svg-icon';
import { AsyncButtonReact } from 'common/components/async-button';
import { formatSamplingFactor } from 'common/report/util';
import { CreateEntityModalReact } from 'common/widgets/create-entity-modal';
import { promptSharing } from '../sharing';
import { getUpsellInfo, ModalTrigger } from '../in-product-upsell-modal/util';
import { promptInProductUpsellModal } from '../in-product-upsell-modal/prompt';
import styles from './index.module.styl';
import { CohortsModalError, CohortsModalState, InlineCohortsModalProps } from './types';

// Lazy load the CohortsBuilderReact component to break a dependency cycle with properties-menu
const CohortsBuilderReact = React.lazy(() => import("common/query-builder/cohorts-query-builder").then(module => ({
  default: module.CohortsBuilderReact
})));

/**
 * Displays a modal for creating, editing, and applying cohorts
 */
export const InlineCohortsModal: React.FC<InlineCohortsModalProps> = _ref => {
  var _currentProject$get4;
  let {
    cohort: cohortProp,
    projectId,
    workspaceId,
    dataGroupId,
    canSave = false,
    canEdit = false,
    canApplyUnsavedCohort = false,
    projectHasCohorts = false,
    supportB2bCohorts = false,
    userId,
    showCohortCount = false,
    trackName,
    name: nameProp = "",
    open = false,
    onClose,
    onChange,
    onTrack,
    onCommitCohort,
    onCommitSavedCohort
  } = _ref;
  const currentProject = useCurrentProject();
  const i18n = useTranslation();
  const {
    t
  } = i18n;
  const [modalState, setModalState] = useState<CohortsModalState>({
    name: nameProp || "",
    showQueryBuilderSkeleton: true,
    showCreateEntityModal: false,
    loadingCohorts: false,
    loadingCohortCount: false,
    errors: new Set<CohortsModalError>(),
    cohortCount: 0,
    cohort: cohortProp ? Cohort.parseCohort(cohortProp) : new Cohort(),
    samplingFactor: 1.0,
    saveCohortAttempted: false
  });

  // Refs for cleanup
  const cohortStoreCallbackRef = useRef<() => void>(() => {});
  const {
    cohort,
    errors,
    loadingCohortCount,
    name,
    showCreateEntityModal,
    cohortCount,
    samplingFactor
  } = modalState;
  const isNewCohort = !cohort.id && !cohort.unsavedId;
  const isNewUnsavedCohort = !cohort.unsavedId;
  const isNewSavedCohort = !cohort.id;
  const updateModalState = (partialState: Partial<CohortsModalState>) => {
    setModalState(prev => _objectSpread(_objectSpread({}, prev), partialState));
  };
  const fetchCohorts = useCallback(() => {
    updateModalState({
      loadingCohorts: true
    });
    cohortStoreCallbackRef.current = () => {
      updateModalState({
        loadingCohorts: false
      });
    };
    cohortsStore.subscribe(cohortStoreCallbackRef.current);
    cohortsStore.fetch({
      projectId,
      workspaceId
    });
  }, [projectId, workspaceId]);
  const fetchCohortCount = useCallback(async (cohortToFetch: Cohort) => {
    if (!showCohortCount) {
      return;
    }
    updateModalState({
      loadingCohortCount: true
    });
    const statsResponse = await fetchPeopleCountByCohort(cohortToFetch, true, dataGroupId, {
      projectId,
      workspaceId
    });
    const count = statsResponse.results;
    if (Number.isInteger(count)) {
      var _statsResponse$metada;
      const factor = (_statsResponse$metada = statsResponse.metadata) === null || _statsResponse$metada === void 0 ? void 0 : _statsResponse$metada.min_sampling_factor;
      updateModalState({
        cohortCount: count,
        loadingCohortCount: false,
        samplingFactor: factor ? factor : 1.0
      });
    }
  }, [dataGroupId, projectId, workspaceId, showCohortCount]);

  // Lifecycle effects
  useEffect(() => {
    // Initial fetch
    fetchCohorts();
    fetchCohortCount(cohort);
    return () => {
      cohortsStore.unsubscribe(cohortStoreCallbackRef.current);
    };
  }, []);
  useEffect(() => {
    const hasCohortWithSelector = cohort && cohort.selector;
    if (hasCohortWithSelector || !modalState.loadingCohorts) {
      updateModalState({
        showQueryBuilderSkeleton: false
      });
    }
  }, [cohort, modalState.loadingCohorts]);
  useEffect(() => {
    if (nameProp) {
      updateModalState({
        name: nameProp
      });
    }
  }, [nameProp]);
  useEffect(() => {
    if (cohortProp) {
      const parsedCohort = Cohort.parseCohort(cohortProp);
      updateModalState({
        cohort: parsedCohort
      });
    }
  }, [cohortProp]);
  const getCohorts = () => Object.values(cohortsStore.value).map((cohortItem: SerializedCohort) => _objectSpread({
    createdBy: cohortItem.created_by,
    lastEdited: cohortItem.last_edited
  }, cohortItem));
  const getCohortNames = () => getCohorts().filter(cohortItem => Number.isInteger(cohortItem.id)).map(_ref2 => {
    let {
      name: cohortName
    } = _ref2;
    return cohortName === null || cohortName === void 0 ? void 0 : cohortName.toLowerCase();
  });
  const saveCohort = async function saveCohort(saveParams: any) {
    let isDuplicate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let savedCohort: SerializedCohort | null = null;
    // merge cohort definition with the save modal name & description
    const cohortToSave = Cohort.parseCohort(_objectSpread(_objectSpread({}, cohort.serialize()), saveParams));
    const isCreate = isNewSavedCohort || isDuplicate;
    try {
      if (isCreate) {
        savedCohort = await cohortsClient.createCohort({
          workspaceId,
          projectId,
          cohort: cohortToSave.serialize()
        });
      } else {
        savedCohort = await cohortsClient.updateCohortById({
          workspaceId,
          projectId,
          cohortID: cohortToSave.id
        }, cohortToSave.serialize());
      }
      const {
        id
      } = savedCohort as Required<Pick<SerializedCohort, `id`>>;
      cohortsStore.update({
        [id]: _objectSpread(_objectSpread({}, savedCohort), {}, {
          count: cohortCount
        })
      });
      if (onCommitSavedCohort) {
        onCommitSavedCohort({
          id,
          name: saveParams.name,
          savedAsNew: isDuplicate,
          cohort: Cohort.parseCohort(_objectSpread(_objectSpread({}, savedCohort), {}, {
            count: cohortCount,
            /** preserve unsavedId in case a temp cohort is saved */
            unsavedId: cohort.unsavedId
          }))
        });
      }
    } catch (_unused) {
      const message = isCreate ? t("users.cohorts.error.failed-create") : t("users.cohorts.error.failed-save");
      addToast({
        type: ToastType.ERROR,
        message
      });
      throw new Error(message);
    }
    return savedCohort;
  };
  const hasUnsavedChanges = () => {
    if (!cohortProp || !cohort) {
      return false;
    }
    const canApplyChanges = canEdit || !cohort.id;
    return canApplyChanges && !Cohort.parseCohort(cohortProp).isEqual(cohort);
  };
  const isSamplingTextEnabled = () => {
    try {
      var _currentProject$get;
      const project = (_currentProject$get = currentProject.get()) === null || _currentProject$get === void 0 ? void 0 : _currentProject$get.value;
      if (!project) {
        return false;
      }
      return project.hasFeatureFlag(ProjectFeatureFlag.SamplingInfo);
    } catch (_unused2) {
      return false;
    }
  };

  // If this is an existing cohort and the name hasn't changed, it's valid. Otherwise check if the name exists in other cohorts
  const hasExistingName = name === nameProp && cohort.id ? false : getCohortNames().includes(name.toLowerCase());
  const getErrorCopy = () => {
    if (errors.has(CohortsModalError.NameRequired)) {
      return t("users.cohorts.error.empty-name");
    } else if (errors.has(CohortsModalError.ExistingName)) {
      return t("users.cohorts.error.name-exists");
    }
    return "";
  };
  const addError = (value: CohortsModalError) => {
    const newErrors = new Set([...errors]);
    newErrors.add(value);
    updateModalState({
      errors: newErrors
    });
  };
  const removeError = (value: CohortsModalError) => {
    const newErrors = new Set([...errors]);
    newErrors.delete(value);
    updateModalState({
      errors: newErrors
    });
  };
  const trackCohortsBuilderEvent = (_ref3: {
    eventName: string;
    props: Record<string, any>;
  }) => {
    let {
      eventName,
      props
    }: {
      eventName: string;
      props: Record<string, any>;
    } = _ref3;
    onTrack === null || onTrack === void 0 || onTrack(eventName, props);
  };
  const isEditableCohort = cohort.canExpandCohortDefinition();
  const getBannerText = () => "".concat(t("users.cohorts.error.no-permission"), " ").concat(!isEditableCohort ? t("users.cohorts.error.try-save-as-new") : "");
  const getTitle = () => {
    if (!open) {
      return "";
    }
    if (cohort.id && !canEdit) {
      return cohort.name;
    }
    return isNewCohort ? t("users.cohorts.create-title") : t("users.cohorts.edit-title");
  };
  const getDisabledEditingReason = (): string => {
    // Attribution Cohorts can contain NthTimeInPeriodFilters so check this first
    // for a more accurate copy message.
    if (cohort.isAttributionCohort()) {
      return t("users.cohorts.error.cant-edit-attribution");
    }
    if (cohort.hasNthTimeInPeriodFilter()) {
      return t("users.cohorts.error.cant-edit-period-filters");
    }
    if (cohort.isReportBased()) {
      const reportName: string = String(cohort.reportName).slice();
      return t("users.cohorts.error.cant-edit-report", {
        reportName: capitalize(reportName)
      });
    } else {
      return "";
    }
  };
  const handleCohortUpdated = useCallback((_ref4: CohortsQueryBuilderUpdate) => {
    let {
      cohort: updatedCohort
    }: CohortsQueryBuilderUpdate = _ref4;
    const newCohort = Cohort.parseCohort(_objectSpread(_objectSpread({}, updatedCohort.serialize()), {}, {
      name
    }));
    updateModalState({
      cohort: newCohort
    });
    fetchCohortCount(newCohort);
  }, [name, fetchCohortCount]);
  const isSavingRestricted = () => {
    var _currentProject$get2;
    const project = (_currentProject$get2 = currentProject.get()) === null || _currentProject$get2 === void 0 ? void 0 : _currentProject$get2.value;
    return !(project !== null && project !== void 0 && project.canSaveEntity(RestrictEntitySaving.Cohorts));
  };
  const openPricingUpsell = () => {
    var _window$open;
    onTrack === null || onTrack === void 0 || onTrack("Click Upsell", {
      'Feature Limit': FeatureGate.RestrictEntitySaving,
      'Entity Type': "cohorts"
    });
    (_window$open = window.open("https://mixpanel.com/pricing/", "_blank")) === null || _window$open === void 0 || _window$open.focus();
  };
  const saveStagedCohortOnInlineModal = async function saveStagedCohortOnInlineModal() {
    let isDuplicate = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (name.trim().length === 0) {
      return addError(CohortsModalError.NameRequired);
    } else if (hasExistingName) {
      return addError(CohortsModalError.ExistingName);
    }
    if (isDuplicate) {
      updateModalState({
        showCreateEntityModal: true
      });
    } else {
      await saveCohort({
        name
      });
    }
  };
  const closeEntityModal = () => updateModalState({
    showCreateEntityModal: false
  });
  const openCohortViewUser = () => {
    const userViewUrl = getReportUrl(ReportName.Users, {
      projectId,
      workspaceId
    }) + Cohort.getExploreHashForUsersInCohort(_objectSpread({
      cohortId: cohort.id,
      cohortName: cohort.name,
      dataGroupId: cohort.dataGroupId
    }, Number.isInteger(cohort.id) ? {} : {
      serializedCohort: cohort.serialize()
    }));
    window.open(userViewUrl);
  };
  const commitCohort = () => {
    if (name.trim().length === 0) {
      return addError(CohortsModalError.NameRequired);
    }
    if (onCommitCohort) {
      onCommitCohort({
        name: cohort.name,
        cohort
      });
    }
  };
  const closeModal = () => {
    updateModalState({
      errors: new Set()
    });
    onClose === null || onClose === void 0 || onClose();
  };

  // @ts-ignore: Parameter 'ev' has an implicit 'any' type
  const saveStagedCohortOnCreateModal = async ev => {
    try {
      const savedCohort = await saveCohort(ev.detail.value, true);
      if (!savedCohort) {
        throw new Error("Failed to save cohort");
      }
      const {
        id
      } = savedCohort;
      if (ev.type === "saveAndShare") {
        await promptSharing({
          type: SharableEntityType.Cohort,
          id,
          'project-id': projectId
        });
      }
    } catch (error) {
      closeModal();
      reporter.error(error);
    }
    closeEntityModal();
  };
  const getModalAttributes = () => {
    return _objectSpread({
      open,
      closeable: true,
      'grow-to-content': true,
      'modal-type': ModalType.Modal
    }, hasUnsavedChanges() ? {
      'close-confirm-text': t("users.cohorts.confirm-unsaved")
    } : {});
  };

  // @ts-ignore: Parameter 'ev' has an implicit 'any' type
  const handleNameInputChange = ev => {
    const {
      value
    } = ev.target;
    if (value && errors.has(CohortsModalError.NameRequired)) {
      removeError(CohortsModalError.NameRequired);
    }
    if (value !== name && errors.has(CohortsModalError.ExistingName)) {
      removeError(CohortsModalError.ExistingName);
    }
    updateModalState({
      name: value,
      cohort: Cohort.parseCohort(_objectSpread(_objectSpread({}, cohort.serialize()), {}, {
        name: value
      }))
    });
  };
  const handleModalStateChange = useCallback(
  // @ts-ignore: Parameter 'ev' has an implicit 'any' type
  ev => {
    const {
      state
    } = ev.detail;
    if (state === "closed") {
      return closeModal();
    }
    onChange === null || onChange === void 0 || onChange(state);
  }, [onChange, closeModal]);
  const shouldRenderUpsellModal = () => {
    var _currentProject$get3;
    return getUpsellInfo(currentProject === null || currentProject === void 0 || (_currentProject$get3 = currentProject.get()) === null || _currentProject$get3 === void 0 ? void 0 : _currentProject$get3.value, ModalTrigger.COHORT, i18n);
  };
  const SaveCohortButton = (_ref5: {
    text: string;
    theme: ButtonTheme;
  }) => {
    let {
      text,
      theme
    }: {
      text: string;
      theme: ButtonTheme;
    } = _ref5;
    return <AsyncButtonReact className={styles.inlineCohortsButton} elref={"saveButton"} theme={theme} asyncAction={async () => {
      if (shouldRenderUpsellModal()) {
        promptInProductUpsellModal(ModalTrigger.COHORT);
      } else {
        await saveStagedCohortOnInlineModal();
      }
    }} data-sentry-element="AsyncButtonReact" data-sentry-component="SaveCohortButton" data-sentry-source-file="index.tsx">
      {shouldRenderUpsellModal() && <SvgIconReact className={styles.buttonIcon} icon={"brand-upgrade"} />}
      {text}
    </AsyncButtonReact>;
  };
  return <div className={styles.inlineCohortsModal} elref={"inlineCohortsModal"} data-sentry-component="InlineCohortsModal" data-sentry-source-file="index.tsx">
      {open && showCreateEntityModal ? <CreateEntityModalReact {...buildCreateEntityModalProps({
      projectId,
      entityType: EntityType.Cohort,
      modalOpen: true,
      isEditing: false,
      isDuplicating: true,
      entity: normalizeCohortEntity(cohort),
      showShareManagement: isSharingEnabledForProject((_currentProject$get4 = currentProject.get()) === null || _currentProject$get4 === void 0 ? void 0 : _currentProject$get4.value) || undefined,
      i18n
    })} onCancel={closeEntityModal} onSave={saveStagedCohortOnCreateModal} onSaveAndShare={saveStagedCohortOnCreateModal} existingEntityNames={getCohortNames()} /> : <ModalReact className={styles.inlineCohortsModal} {...getModalAttributes()} onChange={handleModalStateChange}>
          <section className={styles.inlineCohortsModalScrollWrapper}>
            <section className={styles.inlineCohortsModalHeader}>
              <div className={clsx(styles.mpFontSizeXxl, styles.mpFontWeightBold)} elref={"modalTitle"}>
                {getTitle()}
              </div>
              {cohort.id && <div className={styles.mpFontSizeSmall}>
                  {t("users.cohorts.cohort-id")} {cohort.id}
                </div>}
            </section>
            <section className={styles.inlineCohortsWorkspace}>
              <section className={styles.inlineCohortsBuilder}>
                {modalState.showQueryBuilderSkeleton && <SkeletonListReact />}
                {open && !modalState.showQueryBuilderSkeleton ? <>
                    {!isNewSavedCohort && !canEdit ? <div className={styles.inlineCohortsEditBanner}>
                        <BannerReact theme={BannerTheme.Info} type={BannerType.Embedded} bodyText={getBannerText()} elref={"permissionBanner"} style={{
                  textTransform: "initial"
                }} />
                      </div> : <section className={styles.inlineCohortsNameInput}>
                        <InputReact placeholder={t("users.cohorts.name-placeholder")} maxlength={190} validationStatus={errors.size > 0 ? "invalid" : ""} errorMessage={getErrorCopy()} elref={"inlineCohortsNameInputBox"} value={name} onInput={handleNameInputChange} />
                      </section>}
                    <section className={styles.inlineCohortsBuilderQbWrapper}>
                      {isEditableCohort && modalState.showQueryBuilderSkeleton && <SkeletonListReact />}
                      {isEditableCohort && !modalState.showQueryBuilderSkeleton && <React.Suspense fallback={<SkeletonListReact />}>
                          <CohortsBuilderReact elref={"cohortBuilder"} trackName={trackName} cohort={cohort.serialize()} projectId={projectId} workspaceId={workspaceId} projectHasCohorts={projectHasCohorts} supportB2bCohorts={supportB2bCohorts} dataGroupId={dataGroupId} userId={userId} nonCollapsible onCohortUpdate={handleCohortUpdated} onTrack={trackCohortsBuilderEvent} />
                        </React.Suspense>}
                      {!isEditableCohort && <div className={styles.noCohortDefinition}>
                          <div className={styles.cohortNotEditableText} elref={"cohortNotEditableText"}>
                            {getDisabledEditingReason()}
                          </div>
                          <div>
                            <ButtonReact icon={"share"} elref={"viewUsersButton"} onClick={openCohortViewUser}>
                              {t("users.cohorts.view-users")}
                            </ButtonReact>
                          </div>
                        </div>}
                    </section>
                  </> : null}
                <div className={styles.inlineCohortsBuilderFooter}>
                  <div className={styles.leftControls}>
                    {showCohortCount ? <div className={styles.cohortCount}>
                        {loadingCohortCount ? <SpinnerReact /> : <div elref={"countNumber"}>
                            {t("users.cohorts.user-count", {
                      number: cohortCount.toLocaleString()
                    })}
                            {isSamplingTextEnabled() && samplingFactor < 1.0 ? <div className={styles.cohortResultsSampled}>
                                <SvgIconReact icon={"event-sampling"} />
                                <div className={styles.cohortResultsSampledText}>
                                  {t("sampling.sampled-at", {
                          samplingPercentage: "".concat(formatSamplingFactor(samplingFactor))
                        })}
                                </div>
                              </div> : <></>}
                          </div>}
                      </div> : null}
                    {canSave ? isSavingRestricted() && !shouldRenderUpsellModal() ? <div className={styles.upsellButton}>
                          <TooltipReact placement={Placement.Right}>
                            {t("users.cohorts.upgrade-description")}
                          </TooltipReact>
                          <ButtonReact icon={"share"} onClick={openPricingUpsell}>
                            {t("users.cohorts.upgrade-cta")}
                          </ButtonReact>
                        </div> : null : null}
                  </div>
                  <div className={styles.rightControls}>
                    {/*secondary action*/}
                    {canApplyUnsavedCohort && canSave ? <SaveCohortButton text={isNewUnsavedCohort ? t("users.cohorts.create-and-save") : t("users.cohorts.edit-and-save")} theme={ButtonTheme.Secondary} /> : null}
                    {/*primary action*/}
                    {canApplyUnsavedCohort ? <ButtonReact className={styles.inlineCohortsButton} elref={"inlineCohortsButton"} theme={ButtonTheme.Primary} onClick={commitCohort}>
                        {isNewUnsavedCohort ? t("users.cohorts.create") : t("users.cohorts.edit")}
                      </ButtonReact> : <>
                        {!isNewSavedCohort && !canEdit ? isEditableCohort ? <ButtonReact theme={ButtonTheme.Secondary} elref={"saveButton"} notFocusable onClick={() => saveStagedCohortOnInlineModal(true)}>
                              {t("users.cohorts.save-as-new")}
                            </ButtonReact> : null : <SaveCohortButton text={isNewCohort ? t("users.cohorts.create") : t("common.save")} theme={ButtonTheme.Primary} />}
                      </>}
                  </div>
                </div>
              </section>
            </section>
          </section>
        </ModalReact>}
    </div>;
};